import { Component, ElementRef, HostListener, Input, ViewChild, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-date-select',
  templateUrl: './date-select.component.html',
  styleUrl: './date-select.component.css',
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateSelectComponent),
      multi: true,
    },
  ],
})
export class DateSelectComponent implements ControlValueAccessor {
  @Input() label = '';
  @Input() required = false;
  @Input() placeholder = 'Pick a date';
  @Input() minDate: Date | null = null;
  @Input() maxDate: Date | null = null;

  @ViewChild('container', { static: true }) containerRef?: ElementRef<HTMLElement>;

  selectedDate: Date | null = null;
  showCalendar = false;
  disabled = false;
  isFocused = false;

  onChange: (value: string) => void = () => undefined;
  onTouched: () => void = () => undefined;

  get displayValue(): string {
    if (!this.selectedDate) {
      return '';
    }

    const day = `${this.selectedDate.getDate()}`.padStart(2, '0');
    const month = `${this.selectedDate.getMonth() + 1}`.padStart(2, '0');
    const year = this.selectedDate.getFullYear();
    return `${day}/${month}/${year}`;
  }

  toggleCalendar(): void {
    if (!this.disabled) {
      this.showCalendar = !this.showCalendar;
    }
  }

  onDateChange(date: Date | null): void {
    this.selectedDate = date;
    this.onChange(date ? this.toIsoDate(date) : '');
    this.onTouched();
    this.showCalendar = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.showCalendar || !this.containerRef) {
      return;
    }

    const target = event.target as Node | null;
    if (target && !this.containerRef.nativeElement.contains(target)) {
      this.showCalendar = false;
      this.onTouched();
    }
  }

  writeValue(value: string | Date | null): void {
    if (!value) {
      this.selectedDate = null;
      return;
    }

    if (value instanceof Date) {
      this.selectedDate = Number.isNaN(value.getTime()) ? null : value;
      return;
    }

    const parsed = new Date(value);
    this.selectedDate = Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  private toIsoDate(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
